import { Widget } from 'jamespot-front-business';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { EditorInputColor } from './components/EditorInputColor';
import { EditorInputNumber } from './components/EditorInputNumber';
import { EditorInputSelect } from './components/EditorInputSelect';

import { shallowEqual } from 'react-redux';
import { useTheme } from 'styled-components';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import { commonComponentsRegistry } from '../../registry/CommonRegistry';

const JRCInputText = commonComponentsRegistry.getLazyComponent('InputText');

/*
    Editor tab wrapper (this is all the properties available on a widget wrapper)
*/
export const EditorTabWrapper = ({ uniqid }: { uniqid: string }) => {
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const wrapper = useAppSelector((state) => Widget.selectors.selectWidget(state, uniqid));
    const widgetContext = useAppSelector((state) => Widget.selectors.selectWidgetContext(state, uniqid), shallowEqual);
    const theme = useTheme();

    const styles = useAppSelector((state) => Widget.selectors.selectWidgetTitleStyle(state, uniqid), shallowEqual);

    const { control, watch } = useForm({
        defaultValues: { text: wrapper?.title.replace(/&amp;/g, '&') ?? '' },
        criteriaMode: 'all',
    });

    useEffect(() => {
        const subscription = watch((data: any) => {
            dispatch(Widget.slice.actions.updateWidgetWrapper({ uniqid, title: data.text }));
        });
        return () => subscription.unsubscribe();
    }, [watch, uniqid, dispatch]);

    const initialValues = useMemo(() => {
        return {
            color: styles && styles['color'] ? String(styles['color']) : theme.color.black,
            backgroundColor: styles && styles['background-color'] ? String(styles['background-color']) : 'transparent',
            fontSize: styles && styles['font-size'] ? String(styles['font-size']) : '24',
            fontWeight: styles && styles['font-weight'] ? String(styles['font-weight']) : 'medium',
            textAlign: styles && styles['text-align'] ? String(styles['text-align']) : 'left',
        };
    }, [styles, theme]);

    const handleChange = useCallback(
        (name: string, value: string | number) => {
            dispatch(
                Widget.slice.actions.setWidgetTitleStyle({
                    uniqid,
                    styles: { [name]: value },
                }),
            );
        },
        [dispatch, uniqid],
    );

    return wrapper ? (
        <>
            <JRCInputText
                name={'text'}
                label={'WIDGET_Title'}
                width="100%"
                control={control}
                placeholder={intl.formatMessage({ id: 'WIDGET_Title' })}
            />
            {widgetContext !== 'comment' && (
                <>
                    <EditorInputColor
                        label="STYLE_Text_Color"
                        initialValue={initialValues.color}
                        handleChange={(colorValue) => handleChange('color', colorValue)}
                    />
                    <EditorInputColor
                        label="STYLE_Background_Color"
                        initialValue={initialValues.backgroundColor}
                        handleChange={(backgroundColorValue) => handleChange('background-color', backgroundColorValue)}
                    />
                    <EditorInputNumber
                        label={'STYLE_Text_Size'}
                        initialValue={initialValues.fontSize}
                        handleChange={(fontSizeValue) => handleChange('font-size', fontSizeValue)}
                    />
                    <EditorInputSelect
                        label={'STYLE_Font_Weight'}
                        initialValue={initialValues.fontWeight}
                        options={[
                            { label: intl.formatMessage({ id: 'STYLE_Font_Weight_Normal' }), value: 'normal' },
                            { label: intl.formatMessage({ id: 'STYLE_Font_Weight_Medium' }), value: 'medium' },
                            { label: intl.formatMessage({ id: 'STYLE_Font_Weight_Bold' }), value: 'bold' },
                        ]}
                        required={false}
                        handleChange={(fontWeightValue) => handleChange('font-weight', fontWeightValue)}
                    />
                    <EditorInputSelect
                        label={'STYLE_Text_Align'}
                        initialValue={initialValues.textAlign}
                        options={[
                            { label: intl.formatMessage({ id: 'STYLE_Text_Align_Left' }), value: 'left' },
                            { label: intl.formatMessage({ id: 'STYLE_Text_Align_Center' }), value: 'center' },
                            { label: intl.formatMessage({ id: 'STYLE_Text_Align_Justify' }), value: 'justify' },
                            { label: intl.formatMessage({ id: 'STYLE_Text_Align_Right' }), value: 'right' },
                        ]}
                        required={false}
                        handleChange={(textAlignValue) => handleChange('text-align', textAlignValue)}
                    />
                </>
            )}
        </>
    ) : (
        <></>
    );
};
